import { TEXT_EDITOR_MAX_SIZE, TranslationConstants } from 'src/app/v2/constants';
// Side-effect import: registers the `languagetool` plugin on the global
// `tinymce` object that the self-hosted bundle loaded by src/index.html
// exposes.
import './languagetool-plugin';

// Domain terms the LanguageTool spellcheck must not flag. Successor to the
// premium-spellchecker dictionary removed under PE-054-C (TPX-111); passed to
// the editor as `languagetool_ignore_list`. Matching is case-insensitive (the
// plugin lowercases both sides), so only one casing of each term is listed.
const EDITOR_IGNORED_WORDS = [
  'Exactera',
  'Comparables',
  'Interquartile',
  'NCP',
  'OM',
  'ROA',
  'CP',
  'BR',
  'GM',
  'CPM',
  'TNMM',
  'CUT',
  'CUP',
  'RSP',
  'PLI',
  'OECD',
  'PS',
  'RPM',
  'SCM',
  'Renderer',
  'CUSP',
  'CSP',
  'GSMM',
  'Licensor',
  'Rentee',
  'LLC',
  'GmbH'
];

// Squiggle styles for LanguageTool annotations (red = spelling, blue = grammar).
const LANGUAGETOOL_CONTENT_STYLE = [
  '.lt-error { cursor: pointer; background-size: 4px 2px; background-repeat: repeat-x; background-position: left bottom; }',
  '.lt-error.lt-spelling { background-image: linear-gradient(to right, transparent 0, transparent 25%, #d32f2f 25%, #d32f2f 50%, transparent 50%, transparent 75%, #d32f2f 75%, #d32f2f 100%); }',
  '.lt-error.lt-grammar { background-image: linear-gradient(to right, transparent 0, transparent 25%, #1976d2 25%, #1976d2 50%, transparent 50%, transparent 75%, #1976d2 75%, #1976d2 100%); }'
].join(' ');

export class TinyTextEditor {
  public static FORMAT_ICON = '<svg width="36" height="24" viewBox="0 0 36 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M15 5C15.5523 5 16 5.44772 16 6C16 6.55228 15.5523 7 15 7H11V18C11 18.5523 10.5523 19 10 19C9.44772 19 9 18.5523 9 18V7H5C4.44772 7 4 6.55228 4 6C4 5.44772 4.44772 5 5 5H15Z" /><rect x="21" y="9.41418" width="2" height="9" rx="1" transform="rotate(-45 21 9.41418)" /><rect x="32.364" y="8" width="2" height="9" rx="1" transform="rotate(45 32.364 8)" /></svg>';

  public static INIT = {
    width: '100%',
    height: '300',
    menubar: false,
    branding: false,
    paste_data_images: true, // eslint-disable-line camelcase
    plugins: ['link autolink image table paste lists link image charmap'],
    toolbar: 'formatting sizeandcolor | bullist numlist | aligntext | table | remainder | undo redo',
    // eslint-disable-next-line camelcase
    toolbar_groups: {
      formatting: {
        icon: 'format-icon',
        tooltip: 'Formatting',
        items: 'bold italic underline strikethrough superscript subscript'
      },
      aligntext: {
        icon: 'align-left',
        tooltip: 'alignment',
        items: 'alignleft aligncenter alignright alignjustify'
      },
      tables: {
        icon: 'table',
        tooltip: 'Table',
        items: 'table'
      },
      remainder: {
        icon: 'more-drawer',
        tooltip: 'Additional Options',
        items: 'outdent indent image link charmap removeformat'
      }
    },
    setup: function (editor) {
      editor.ui.registry.addIcon('format-icon', TinyTextEditor.FORMAT_ICON);
      editor.ui.registry.addMenuButton('sizeandcolor', {
        icon: 'change-case',
        fetch: (callback: (items: string) => void) => {
          const items = 'blockformats fontsizes forecolor backcolor';
          callback(items);
        }
      });
      editor.on('blur', function (event) {
        const numChars = editor.getContent().length;
        if (numChars > TEXT_EDITOR_MAX_SIZE) {
          // eslint-disable-next-line no-alert
          alert(TranslationConstants.ERROR_MAX_TINYMCE_FILESIZE_EXCEEDED);
          event.preventDefault();
          return false;
        }
      });
    }
  };

  private static languageToolEnabled = false;

  /**
   * Builds the self-hosted LanguageTool endpoint for an environment. The infra
   * module exposes the server at `languagetool.${xbs_environment_short}xbs.com`
   * (no prod special-case). `|| 'dev'` (not `??`) so an empty-string or
   * undefined short falls back rather than producing a malformed
   * `languagetool.xbs.com`. Pure — extracted so it's unit-testable.
   */
  public static buildLanguageToolUrl(envShort: string): string {
    return `https://languagetool.${envShort || 'dev'}xbs.com/v2/check`;
  }

  /**
   * Wires the LanguageTool spellcheck into the shared editor INIT. Called
   * once from AppComponent when the LaunchDarkly flag
   * `editor-languagetool-spellcheck` (JPD-177) evaluates true. Components
   * hold a reference to TinyTextEditor.INIT, so editors initialized after
   * this call pick the plugin up without any per-component changes; with the
   * flag OFF this never runs and the editor behaves exactly as today.
   */
  public static enableLanguageTool(languageToolUrl: string): void {
    if (TinyTextEditor.languageToolEnabled) {
      return;
    }
    TinyTextEditor.languageToolEnabled = true;

    const init = TinyTextEditor.INIT as any;
    init.plugins = init.plugins.concat(['languagetool']);
    init.toolbar = init.toolbar.replace('| remainder |', '| languagetool | remainder |');
    /* eslint-disable camelcase */
    init.languagetool_url = languageToolUrl;
    init.languagetool_language = 'en-US';
    init.languagetool_ignore_list = EDITOR_IGNORED_WORDS;
    // Stays false (TinyMCE default): Chrome/Edge enhanced spellcheck ships
    // editor contents to Google/Microsoft (spell-jacking); LanguageTool keeps
    // the data in-VPC.
    init.browser_spellcheck = false;
    if (init.content_style) {
      init.content_style = init.content_style + ' ' + LANGUAGETOOL_CONTENT_STYLE;
    } else {
      init.content_style = LANGUAGETOOL_CONTENT_STYLE;
    }
    /* eslint-enable camelcase */
  }
}
